import React, { useState, useMemo, useEffect } from 'react';
import { useApi, api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import LogViewer from '../components/LogViewer';
import useMobile from '../hooks/useMobile';

// ─── Hjelpere ─────────────────────────────────────────────────
function Avatar({ name, color, size = 34 }) {
  const initials = (name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:color||'#7C6FF7',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontWeight:700, fontSize:size*0.38, color:'#fff', flexShrink:0 }}>
      {initials}
    </div>
  );
}

function timeSince(dateStr) {
  if (!dateStr) return 'Aldri';
  const mins = Math.floor((Date.now() - new Date(dateStr)) / 60000);
  if (mins < 2)   return 'Akkurat nå';
  if (mins < 60)  return `${mins}m siden`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}t siden`;
  const days = Math.floor(hrs / 24);
  if (days < 7)   return `${days}d siden`;
  return new Date(dateStr).toLocaleDateString('nb-NO', { day:'numeric', month:'short', year:'numeric' });
}

function fmtUptime(sec) {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}t`;
  if (h > 0) return `${h}t ${m}m`;
  return `${m}m`;
}

// ─── Paginering ───────────────────────────────────────────────
function usePagination(items, pageSize) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const pageItems  = items.slice((safePage - 1) * pageSize, safePage * pageSize);
  return { page: safePage, setPage, pageItems, totalPages, total: items.length };
}

function Paginator({ page, totalPages, total, pageSize, setPage }) {
  if (totalPages <= 1) return null;
  const from  = (page - 1) * pageSize + 1;
  const to    = Math.min(page * pageSize, total);
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
    .reduce((acc, n, i, arr) => {
      if (i > 0 && n - arr[i - 1] > 1) acc.push('…');
      acc.push(n);
      return acc;
    }, []);
  return (
    <div style={s.paginator}>
      <span style={s.paginatorInfo}>Viser {from}–{to} av {total}</span>
      <div style={s.paginatorBtns}>
        <button style={{ ...s.pageBtn, opacity: page > 1 ? 1 : 0.35 }}
          disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
        {pages.map((p, i) =>
          p === '…'
            ? <span key={`e${i}`} style={s.pageDots}>…</span>
            : <button key={p} style={{ ...s.pageBtn, ...(p === page ? s.pageBtnActive : {}) }}
                onClick={() => setPage(p)}>{p}</button>
        )}
        <button style={{ ...s.pageBtn, opacity: page < totalPages ? 1 : 0.35 }}
          disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>→</button>
      </div>
    </div>
  );
}

const STATUS_LABELS = { open:'🟡 Åpen', in_progress:'🔵 Pågår', resolved:'🟢 Løst', closed:'⚫ Lukket' };
const STATUS_BG     = { open:'#FEF3C7', in_progress:'#DBEAFE', resolved:'#D1FAE5', closed:'#F0EEF8' };
const STATUS_COLOR  = { open:'#92400E', in_progress:'#1E3A5F', resolved:'#065F46', closed:'#3A3A5C' };
const PRIO_COLOR    = { high:'#DC2626', urgent:'#DC2626', normal:'#6B6B8A', low:'#A0A0B8' };

// ─── Widget-wrapper ───────────────────────────────────────────
function Widget({ icon, title, children, badge, gridColumn, action }) {
  return (
    <div style={{ ...s.widget, ...(gridColumn ? { gridColumn } : {}) }}>
      <div style={s.widgetHeader}>
        <span style={s.widgetIcon}>{icon}</span>
        <span style={s.widgetTitle}>{title}</span>
        {badge != null && badge > 0 && (
          <span style={s.widgetBadge}>{badge > 99 ? '99+' : badge}</span>
        )}
        {action}
      </div>
      <div style={s.widgetBody}>{children}</div>
    </div>
  );
}

// ─── Hoved-komponent ─────────────────────────────────────────
export default function AdminPage() {
  const { user } = useAuth();
  const isMobile = useMobile();
  const [flash,        setFlash]        = useState(null);
  const [wide,         setWide]         = useState(() => window.innerWidth > 780);

  useEffect(() => {
    const fn = () => setWide(window.innerWidth > 780);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const { data, refresh }              = useApi('/api/admin/overview');
  const { data: sysInfo, refresh: refreshSys } = useApi('/api/admin/system/info');
  const families = data?.families || [];
  const allUsers = data?.users    || [];
  const stats    = data?.stats    || {};

  const notify = (text, ok = true) => {
    setFlash({ text, ok });
    setTimeout(() => setFlash(null), 3000);
  };

  if (!user?.superadmin) {
    return <div style={s.noAccess}>🚫 Ingen tilgang</div>;
  }

  return (
    <div style={{ maxWidth:1100, boxSizing:'border-box', padding: isMobile ? '0 14px 24px' : undefined }}>
      {flash && (
        <div style={{ ...s.flash, background: flash.ok ? '#34D399' : '#F7605C' }}>
          {flash.text}
        </div>
      )}

      {/* Gradient Hero Header */}
      <div style={{ background:'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)', borderRadius:20, padding:'28px 24px 24px', marginBottom:20, position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-50, left:-20, width:130, height:130, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }}/>
        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ fontSize:12, color:'rgba(255,255,255,0.65)', fontWeight:600, marginBottom:4, letterSpacing:1, textTransform:'uppercase' }}>Famora</div>
          <div style={{ fontSize:26, fontWeight:800, color:'#fff', marginBottom:4 }}>🛡️ Systemadmin</div>
          <div style={{ fontSize:13, color:'rgba(255,255,255,0.72)' }}>{user?.name} · Superadmin</div>
        </div>
      </div>

      {wide ? (
        <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
          {/* ── Venstre kolonne ── */}
          <div style={{ flex:1, display:'flex', flexDirection:'column', gap:14 }}>
            <StatsWidget stats={stats} />
            <SupportWidget data={data} refresh={refresh} notify={notify} />
            <FamiliesWidget families={families} user={user} notify={notify} refresh={refresh} />
          </div>

          {/* ── Høyre kolonne ── */}
          <div style={{ flex:1, display:'flex', flexDirection:'column', gap:14 }}>
            <SystemWidget sysInfo={sysInfo} refresh={refreshSys} />
            <BroadcastWidget notify={notify} />
            <RecentUsersWidget sysInfo={sysInfo} />
            <UsersWidget allUsers={allUsers} user={user} notify={notify} refresh={refresh} />
          </div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <StatsWidget stats={stats} />
          <SystemWidget sysInfo={sysInfo} refresh={refreshSys} />
          <SupportWidget data={data} refresh={refresh} notify={notify} />
          <BroadcastWidget notify={notify} />
          <RecentUsersWidget sysInfo={sysInfo} />
          <FamiliesWidget families={families} user={user} notify={notify} refresh={refresh} />
          <UsersWidget allUsers={allUsers} user={user} notify={notify} refresh={refresh} />
        </div>
      )}

      {/* Systemlogg */}
      <div style={{ marginTop:20, background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:16, padding:20 }}>
        <h2 style={{ fontSize:16, fontWeight:800, color:'var(--txt)', margin:'0 0 16px' }}>📋 Systemlogg</h2>
        <LogViewer />
      </div>
    </div>
  );
}

// ─── Stats-widget ─────────────────────────────────────────────
function StatsWidget({ stats }) {
  const items = [
    { val: stats.families      || 0, lbl: 'Familier',  color:'#7C6FF7', icon:'🏠' },
    { val: stats.users         || 0, lbl: 'Brukere',   color:'#1A1A35', icon:'👤' },
    { val: stats.active_users  || 0, lbl: 'Aktive',    color:'#34D399', icon:'✅' },
    { val: stats.new_this_month|| 0, lbl: 'Nye (mnd)', color:'#FBBF24', icon:'🆕' },
    { val: stats.superadmins   || 0, lbl: 'Superadmin',color:'#F472B6', icon:'⭐' },
  ];
  return (
    <Widget icon="📊" title="Statistikk">
      <div style={s.statsGrid}>
        {items.map(({ val, lbl, color, icon }) => (
          <div key={lbl} style={s.statCard}>
            <div style={s.statIcon}>{icon}</div>
            <div style={{ ...s.statVal, color }}>{val}</div>
            <div style={s.statLbl}>{lbl}</div>
          </div>
        ))}
      </div>
    </Widget>
  );
}

// ─── System-widget ────────────────────────────────────────────
function SystemWidget({ sysInfo, refresh }) {
  const [cleaning, setCleaning] = useState(false);
  const [cleanMsg, setCleanMsg] = useState('');

  const cleanup = async () => {
    if (!window.confirm('Slette alle lukkede saker eldre enn 30 dager?')) return;
    setCleaning(true);
    try {
      const r = await api.delete('/api/admin/support/cleanup?days=30');
      setCleanMsg(`✅ ${r.deleted} saker slettet`);
      setTimeout(() => setCleanMsg(''), 4000);
    } catch { setCleanMsg('❌ Feilet'); }
    finally { setCleaning(false); }
  };

  return (
    <Widget icon="⚙️" title="Systemhelse"
      action={<button style={s.headerBtn} onClick={refresh}>↻</button>}>
      {!sysInfo ? (
        <div style={s.emptyBody}>Laster…</div>
      ) : (
        <div style={s.widgetContent}>
          <div style={s.infoGrid}>
            {[
              { lbl:'Oppetid',    val: fmtUptime(sysInfo.uptime) },
              { lbl:'Minne',      val: `${sysInfo.memory_mb} MB` },
              { lbl:'Node',       val: sysInfo.node_version },
              { lbl:'Familier',   val: sysInfo.counts?.families },
              { lbl:'Brukere',    val: sysInfo.counts?.users },
              { lbl:'Meldinger',  val: sysInfo.counts?.messages },
              { lbl:'Hendelser',  val: sysInfo.counts?.events },
              { lbl:'Kontakter',  val: sysInfo.counts?.contacts },
              { lbl:'Support',    val: sysInfo.counts?.tickets, highlight: sysInfo.counts?.tickets_open > 0 },
            ].map(({ lbl, val, highlight }) => (
              <div key={lbl} style={{ ...s.infoRow, ...(highlight ? s.infoRowHighlight : {}) }}>
                <span style={s.infoLbl}>{lbl}</span>
                <span style={{ ...s.infoVal, ...(highlight ? { color:'#F7605C', fontWeight:800 } : {}) }}>{val}</span>
              </div>
            ))}
          </div>
          {cleanMsg && <div style={s.cleanMsg}>{cleanMsg}</div>}
          <button style={s.dangerBtn} onClick={cleanup} disabled={cleaning}>
            {cleaning ? 'Rydder…' : '🗑 Rydd lukkede saker (30d+)'}
          </button>
        </div>
      )}
    </Widget>
  );
}

// ─── Kringkast-widget ─────────────────────────────────────────
function BroadcastWidget({ notify }) {
  const [form,     setForm]     = useState({ title:'', body:'' });
  const [sending,  setSending]  = useState(false);

  const send = async () => {
    if (!form.title.trim()) return notify('Tittel er påkrevd', false);
    if (!window.confirm(`Sende push-varsel til ALLE brukere?\n\n"${form.title}"`)) return;
    setSending(true);
    try {
      const r = await api.post('/api/admin/broadcast', form);
      notify(`✅ Sendt til ${r.sent} enheter (${r.failed} feilet)`);
      setForm({ title:'', body:'' });
    } catch (ex) {
      notify(ex.response?.data?.error || 'Sending feilet', false);
    } finally { setSending(false); }
  };

  return (
    <Widget icon="📢" title="Kringkast til alle brukere">
      <div style={s.widgetContent}>
        <div style={s.hint}>Send push-varsel til alle innloggede enheter på tvers av alle familier.</div>
        <input style={s.inp} placeholder="Tittel *" value={form.title}
          onChange={e => setForm(f => ({...f, title:e.target.value}))} maxLength={80} />
        <input style={s.inp} placeholder="Melding (valgfritt)" value={form.body}
          onChange={e => setForm(f => ({...f, body:e.target.value}))} maxLength={200} />
        <button style={s.primaryBtn} onClick={send} disabled={sending}>
          {sending ? 'Sender…' : '📢 Send til alle'}
        </button>
      </div>
    </Widget>
  );
}

// ─── Siste registreringer ─────────────────────────────────────
function RecentUsersWidget({ sysInfo }) {
  const recent = sysInfo?.recentUsers || [];
  return (
    <Widget icon="🆕" title="Siste registreringer">
      {recent.length === 0 ? (
        <div style={s.emptyBody}>Ingen data ennå</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column' }}>
          {recent.map((u, i) => (
            <div key={i} style={s.recentRow}>
              <div style={s.recentAvatar}>{(u.name||'?')[0].toUpperCase()}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={s.recentName}>{u.name}</div>
                <div style={s.recentMeta}>{u.email}</div>
              </div>
              <div style={s.recentTime}>{timeSince(u.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </Widget>
  );
}

// ─── Support-widget ───────────────────────────────────────────
const SUPPORT_PAGE_SIZE = 8;

function SupportWidget({ data, refresh, notify }) {
  const { data: ticketData, refresh: refreshTickets } = useApi('/api/support/all');
  const [expanded, setExpanded] = useState(null);
  const [reply,    setReply]    = useState({});
  const [status,   setStatus]   = useState({});
  const [filter,   setFilter]   = useState('open');

  const tickets   = ticketData || [];
  const openCount = tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length;
  const filtered  = filter === 'all' ? tickets : tickets.filter(t => t.status === filter);

  const { page, setPage, pageItems, totalPages, total } = usePagination(filtered, SUPPORT_PAGE_SIZE);

  // Reset til side 1 når filter endres
  useEffect(() => { setPage(1); }, [filter, setPage]);

  const respond = async (id) => {
    try {
      await api.put(`/api/support/${id}`, {
        admin_response: reply[id]?.trim() || undefined,
        status: status[id] || undefined,
      });
      notify('Svar lagret');
      refreshTickets();
      setExpanded(null);
    } catch { notify('Feil ved lagring', false); }
  };

  return (
    <Widget icon="🎫" title="Support-saker" badge={openCount}>
      <div style={s.widgetContent}>
        <div style={s.filterRow}>
          {[['open','🟡 Åpne'],['in_progress','🔵 Pågår'],['resolved','🟢 Løst'],['closed','⚫ Lukket'],['all','Alle']].map(([f,lbl]) => {
            const cnt = f === 'all' ? tickets.length : tickets.filter(t => t.status === f).length;
            return (
              <button key={f}
                style={{ ...s.filterBtn, ...(filter===f ? s.filterBtnActive : {}) }}
                onClick={() => setFilter(f)}>
                {lbl} {cnt > 0 && <span style={s.filterCount}>{cnt}</span>}
              </button>
            );
          })}
        </div>

        {filtered.length === 0 && <div style={s.emptyBody}>Ingen saker</div>}

        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {pageItems.map(t => (
            <div key={t.id} style={{
              ...s.ticketCard,
              borderLeft: t.priority === 'high' || t.priority === 'urgent' ? '3px solid #F7605C' : '3px solid transparent'
            }}>
              <div style={s.ticketTop} onClick={() => setExpanded(e => e===t.id ? null : t.id)}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                    <span style={s.ticketTitle}>{t.title}</span>
                    <span style={{
                      padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700,
                      background: STATUS_BG[t.status]||'#F0EEF8',
                      color: STATUS_COLOR[t.status]||'#6B6B8A'
                    }}>{STATUS_LABELS[t.status]||t.status}</span>
                    {(t.priority==='high'||t.priority==='urgent') && (
                      <span style={{ fontSize:10, fontWeight:800, color:'#F7605C' }}>
                        {t.priority==='urgent'?'🆘':'⚠️'} {t.priority}
                      </span>
                    )}
                  </div>
                  <div style={s.ticketMeta}>
                    {t.type} · {t.family_name||'—'} · {t.user_name||'—'} · {new Date(t.created_at).toLocaleDateString('nb-NO')}
                  </div>
                </div>
                <span style={{ fontSize:10, color:'#A0A0B8', flexShrink:0 }}>{expanded===t.id?'▲':'▼'}</span>
              </div>

              {expanded === t.id && (
                <div style={s.ticketDetail}>
                  <div style={s.ticketDesc}>{t.description}</div>
                  {t.admin_response && (
                    <div style={s.adminReply}><strong>Svar:</strong> {t.admin_response}</div>
                  )}
                  <select style={s.sel}
                    value={status[t.id]||t.status}
                    onChange={e => setStatus(st => ({...st,[t.id]:e.target.value}))}>
                    {Object.keys(STATUS_LABELS).map(k => <option key={k} value={k}>{STATUS_LABELS[k]}</option>)}
                  </select>
                  <textarea style={s.textarea}
                    placeholder="Skriv svar til brukeren…"
                    value={reply[t.id]||''}
                    onChange={e => setReply(r => ({...r,[t.id]:e.target.value}))} />
                  <button style={s.primaryBtn} onClick={() => respond(t.id)}>
                    Lagre svar og status
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <Paginator page={page} totalPages={totalPages} total={total}
        pageSize={SUPPORT_PAGE_SIZE} setPage={setPage} />
    </Widget>
  );
}

// ─── Familier-widget ──────────────────────────────────────────
const FAMILIES_PAGE_SIZE = 10;

function FamiliesWidget({ families, user, notify, refresh }) {
  const [expanded, setExpanded] = useState(null);
  const [search,   setSearch]   = useState('');

  const filtered = search
    ? families.filter(f => f.name?.toLowerCase().includes(search.toLowerCase()))
    : families;

  const { page, setPage, pageItems, totalPages, total } = usePagination(filtered, FAMILIES_PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search, setPage]);

  const deleteFamily = async (f) => {
    if (!window.confirm(`Slette "${f.name}" og ALL data permanent? Dette kan ikke angres.`)) return;
    await api.delete(`/api/admin/families/${f.id}`);
    setExpanded(null);
    notify(`Familien "${f.name}" slettet`);
    refresh();
  };

  return (
    <Widget icon="🏠" title="Familieoversikt"
      action={<span style={s.headerMeta}>{families.length} familier</span>}>
      <div style={s.widgetContent}>
        <input style={s.inp} placeholder="Søk familie…" value={search}
          onChange={e => setSearch(e.target.value)} />
        {filtered.length === 0 && <div style={s.emptyBody}>Ingen familier</div>}
        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
          {pageItems.map(f => (
            <div key={f.id} style={s.famCard}>
              <div style={s.famTop} onClick={() => setExpanded(e => e===f.id ? null : f.id)}>
                <span style={s.famIcon}>🏠</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={s.famName}>{f.name}</div>
                  <div style={s.famMeta}>{f.member_count} {f.member_count===1?'bruker':'brukere'} · {new Date(f.created_at).toLocaleDateString('nb-NO')}</div>
                </div>
                <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                  {f.id !== user.family_id && (
                    <button style={s.delBtn} onClick={e => { e.stopPropagation(); deleteFamily(f); }}>
                      Slett
                    </button>
                  )}
                  <span style={{ fontSize:10, color:'#A0A0B8' }}>{expanded===f.id?'▲':'▼'}</span>
                </div>
              </div>
              {expanded === f.id && (
                <div style={s.memberList}>
                  {(f.members||[]).map(m => (
                    <div key={m.id} style={{ ...s.memberRow, opacity:m.active===0?0.5:1 }}>
                      <Avatar name={m.name} color={m.color} size={28} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={s.memberName}>
                          {m.name}
                          {m.role==='admin'  && <span style={s.badge}>Admin</span>}
                          {m.superadmin      && <span style={s.superBadge}>⭐</span>}
                          {m.active===0      && <span style={s.inactiveBadge}>Inaktiv</span>}
                        </div>
                        <div style={s.memberMeta}>{m.email} · {timeSince(m.last_seen)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <Paginator page={page} totalPages={totalPages} total={total}
        pageSize={FAMILIES_PAGE_SIZE} setPage={setPage} />
    </Widget>
  );
}

// ─── Brukere-widget ───────────────────────────────────────────
const USERS_PAGE_SIZE = 15;

function UsersWidget({ allUsers, user, notify, refresh }) {
  const [search,      setSearch]      = useState('');
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter(u =>
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.family_name?.toLowerCase().includes(q)
    );
  }, [allUsers, search]);

  const { page, setPage, pageItems, totalPages, total } = usePagination(filtered, USERS_PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search, setPage]);

  const toggleActive = async (u) => {
    const next = !u.active;
    await api.put(`/api/admin/users/${u.id}/active`, { active: next });
    notify(`${u.name} ${next ? 'aktivert' : 'deaktivert'}`);
    refresh();
  };

  const toggleSuperadmin = async (u) => {
    if (u.id === user.id && u.superadmin && !window.confirm('Fjerne din egen superadmin-tilgang?')) return;
    const next = !u.superadmin;
    await api.put(`/api/admin/users/${u.id}/superadmin`, { superadmin: next });
    notify(`Superadmin ${next ? 'tildelt' : 'fjernet'} for ${u.name}`);
    refresh();
  };

  const doResetPassword = async (u) => {
    if (newPassword.length < 8) { notify('Passord må være minst 8 tegn', false); return; }
    await api.put(`/api/admin/users/${u.id}/password`, { password: newPassword });
    setResetTarget(null); setNewPassword('');
    notify(`Passord tilbakestilt for ${u.name}`);
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Slette ${u.name} permanent?`)) return;
    await api.delete(`/api/admin/users/${u.id}`);
    notify(`${u.name} slettet`);
    refresh();
  };

  return (
    <Widget icon="👤" title="Brukeradministrasjon"
      action={<span style={s.headerMeta}>{allUsers.length} brukere</span>}>
      <div style={s.widgetContent}>
        <input style={s.inp} placeholder="Søk navn, e-post eller familie…"
          value={search} onChange={e => setSearch(e.target.value)} />
        {search && <div style={s.searchCount}>{filtered.length} treff for «{search}»</div>}

        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
          {pageItems.length === 0 && <div style={s.emptyBody}>Ingen brukere funnet</div>}
          {pageItems.map(u => (
            <div key={u.id} style={{ ...s.userCard, opacity:u.active===0?0.6:1 }}>
              <div style={s.userRow}>
                <Avatar name={u.name} color={u.color} size={36} />
                <div style={s.userInfo}>
                  <div style={s.userName}>
                    {u.name}
                    {u.role==='admin'  && <span style={s.badge}>Admin</span>}
                    {u.superadmin      && <span style={s.superBadge}>⭐ Super</span>}
                    {u.active===0      && <span style={s.inactiveBadge}>Inaktiv</span>}
                  </div>
                  <div style={s.userMeta}>{u.email} · {u.family_name} · {timeSince(u.last_seen)}</div>
                </div>
                <div style={s.actions}>
                  {resetTarget === u.id ? (
                    <>
                      <input style={s.resetInp} type="password" placeholder="Nytt passord"
                        value={newPassword} onChange={e => setNewPassword(e.target.value)}
                        autoFocus onKeyDown={e => e.key==='Enter' && doResetPassword(u)} />
                      <button style={{ ...s.iconBtn, background:'#D1FAE5' }} onClick={() => doResetPassword(u)}>✓</button>
                      <button style={{ ...s.iconBtn }} onClick={() => { setResetTarget(null); setNewPassword(''); }}>✕</button>
                    </>
                  ) : (
                    <>
                      <button style={{ ...s.iconBtn, background: u.active===0 ? '#D1FAE5' : '#FEF3C7' }}
                        onClick={() => toggleActive(u)} title={u.active===0?'Aktiver':'Deaktiver'}>
                        {u.active===0 ? '🔓' : '🔒'}
                      </button>
                      <button style={{ ...s.iconBtn, background: u.superadmin ? '#EDE9FE' : '#F5F4FC' }}
                        onClick={() => toggleSuperadmin(u)} title={u.superadmin?'Fjern superadmin':'Gi superadmin'}>
                        ⭐
                      </button>
                      <button style={{ ...s.iconBtn, background:'#F5F4FC' }}
                        onClick={() => { setResetTarget(u.id); setNewPassword(''); }} title="Tilbakestill passord">
                        🔑
                      </button>
                      {u.id !== user.id && (
                        <button style={{ ...s.iconBtn, background:'#FEE2E2' }}
                          onClick={() => deleteUser(u)} title="Slett bruker">
                          🗑
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Paginator page={page} totalPages={totalPages} total={total}
        pageSize={USERS_PAGE_SIZE} setPage={setPage} />
    </Widget>
  );
}

// ─── Styles ───────────────────────────────────────────────────
const s = {
  noAccess:     { padding:40, textAlign:'center', color:'var(--txl)', fontSize:16 },
  flash:        { position:'fixed', top:16, right:16, zIndex:999, padding:'10px 18px', borderRadius:10, color:'#fff', fontWeight:700, fontSize:14, boxShadow:'0 4px 16px rgba(0,0,0,.15)' },

  widget:       { background:'var(--card)', borderRadius:16, border:'1px solid var(--bdr)', overflow:'hidden', boxShadow:'0 4px 24px rgba(124,111,247,0.08), 0 1px 3px rgba(0,0,0,0.04)' },
  widgetHeader: { display:'flex', alignItems:'center', gap:10, padding:'13px 16px', borderBottom:'1px solid var(--bdr)', background:'rgba(124,111,247,0.05)' },
  widgetIcon:   { fontSize:16, flexShrink:0 },
  widgetTitle:  { fontSize:13, fontWeight:800, color:'var(--txt)', flex:1 },
  widgetBadge:  { minWidth:20, height:20, borderRadius:10, background:'#F7605C', color:'#fff', fontSize:11, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 5px' },
  widgetBody:   {},
  widgetContent:{ padding:'14px 16px', display:'flex', flexDirection:'column', gap:10 },
  headerBtn:    { background:'none', border:'1px solid var(--bdr)', borderRadius:7, padding:'3px 8px', cursor:'pointer', fontSize:13, color:'var(--txl)', fontFamily:'inherit' },
  headerMeta:   { fontSize:11, color:'var(--txl)', fontWeight:600 },

  statsGrid:    { display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:8, padding:'14px 16px' },
  statCard:     { background:'rgba(124,111,247,0.08)', borderRadius:12, padding:'12px 8px', textAlign:'center', border:'1px solid rgba(124,111,247,0.15)' },
  statIcon:     { fontSize:18, marginBottom:4 },
  statVal:      { fontSize:22, fontWeight:800, lineHeight:1.1, color:'var(--txt)' },
  statLbl:      { fontSize:10, fontWeight:700, color:'var(--txl)', textTransform:'uppercase', letterSpacing:.3, marginTop:3 },

  infoGrid:     { display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 },
  infoRow:      { display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid var(--bdr)', fontSize:13 },
  infoRowHighlight: { background:'rgba(229,83,75,0.08)', borderRadius:6, padding:'6px 8px' },
  infoLbl:      { color:'var(--txl)', fontWeight:600 },
  infoVal:      { color:'var(--txt)', fontWeight:700 },

  hint:         { fontSize:12, color:'var(--txl)', lineHeight:1.6 },
  inp:          { padding:'9px 12px', borderRadius:9, border:'1px solid var(--bdr)', fontFamily:'inherit', fontSize:13, color:'var(--txt)', outline:'none', background:'var(--input-bg)', width:'100%', boxSizing:'border-box' },
  sel:          { padding:'8px 10px', borderRadius:9, border:'1px solid var(--bdr)', fontFamily:'inherit', fontSize:12, color:'var(--txt)', outline:'none', background:'var(--input-bg)', cursor:'pointer', width:'100%' },
  textarea:     { padding:'8px 12px', borderRadius:9, border:'1px solid var(--bdr)', fontFamily:'inherit', fontSize:13, color:'var(--txt)', outline:'none', resize:'vertical', minHeight:72, width:'100%', boxSizing:'border-box', background:'var(--input-bg)' },
  primaryBtn:   { padding:'10px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'inherit', width:'100%' },
  dangerBtn:    { padding:'8px 14px', borderRadius:9, border:'1px solid rgba(229,83,75,0.3)', background:'rgba(229,83,75,0.08)', color:'#E5534B', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit', width:'100%' },
  cleanMsg:     { fontSize:12, padding:'6px 10px', borderRadius:8, background:'rgba(52,211,153,0.12)', color:'#34D399', fontWeight:600 },
  emptyBody:    { padding:'20px 16px', textAlign:'center', color:'var(--txl)', fontSize:13 },
  searchCount:  { fontSize:11, color:'var(--txl)' },

  filterRow:    { display:'flex', gap:4, flexWrap:'wrap' },
  filterBtn:    { padding:'4px 10px', borderRadius:20, border:'1px solid var(--bdr)', background:'rgba(124,111,247,0.08)', color:'var(--txm)', fontWeight:700, fontSize:11, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:4 },
  filterBtnActive: { background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', border:'none' },
  filterCount:  { background:'rgba(255,255,255,.25)', borderRadius:10, minWidth:16, textAlign:'center', padding:'0 4px' },

  ticketCard:   { background:'var(--card2)', borderRadius:10, overflow:'hidden', border:'1px solid var(--bdr)' },
  ticketTop:    { display:'flex', alignItems:'center', gap:8, padding:'10px 12px', cursor:'pointer', userSelect:'none' },
  ticketTitle:  { fontSize:13, fontWeight:700, color:'var(--txt)' },
  ticketMeta:   { fontSize:11, color:'var(--txl)', marginTop:2 },
  ticketDetail: { borderTop:'1px solid var(--bdr)', padding:'10px 12px', display:'flex', flexDirection:'column', gap:8 },
  ticketDesc:   { fontSize:13, color:'var(--txm)', background:'rgba(124,111,247,0.06)', borderRadius:8, padding:'8px 12px', lineHeight:1.6 },
  adminReply:   { fontSize:13, background:'rgba(52,211,153,0.12)', borderRadius:8, padding:'8px 12px', color:'#34D399' },

  famCard:      { background:'var(--card2)', borderRadius:10, border:'1px solid var(--bdr)', overflow:'hidden' },
  famTop:       { display:'flex', alignItems:'center', gap:10, padding:'10px 12px', cursor:'pointer', userSelect:'none' },
  famIcon:      { fontSize:18, flexShrink:0 },
  famName:      { fontSize:13, fontWeight:800, color:'var(--txt)' },
  famMeta:      { fontSize:11, color:'var(--txl)', marginTop:1 },
  delBtn:       { padding:'4px 10px', borderRadius:7, border:'1px solid rgba(229,83,75,0.3)', background:'rgba(229,83,75,0.08)', color:'#E5534B', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit' },
  memberList:   { borderTop:'1px solid var(--bdr)', display:'flex', flexDirection:'column' },
  memberRow:    { display:'flex', alignItems:'center', gap:8, padding:'8px 12px', borderBottom:'1px solid var(--bdr)' },
  memberName:   { fontSize:12, fontWeight:700, color:'var(--txt)', display:'flex', alignItems:'center', gap:4, flexWrap:'wrap' },
  memberMeta:   { fontSize:11, color:'var(--txl)', marginTop:1 },
  badge:        { fontSize:9, fontWeight:700, padding:'2px 6px', borderRadius:10, background:'rgba(124,111,247,0.15)', color:'#7C6FF7' },
  superBadge:   { fontSize:9, fontWeight:700, padding:'2px 6px', borderRadius:10, background:'rgba(251,191,36,0.15)', color:'#D97706' },
  inactiveBadge:{ fontSize:9, fontWeight:700, padding:'2px 6px', borderRadius:10, background:'rgba(229,83,75,0.12)', color:'#E5534B' },

  userCard:     { background:'var(--card2)', borderRadius:10, border:'1px solid var(--bdr)' },
  userRow:      { display:'flex', alignItems:'center', gap:10, padding:'10px 12px', flexWrap:'wrap' },
  userInfo:     { flex:1, minWidth:0 },
  userName:     { fontSize:13, fontWeight:700, color:'var(--txt)', display:'flex', alignItems:'center', gap:4, flexWrap:'wrap' },
  userMeta:     { fontSize:11, color:'var(--txl)', marginTop:1 },
  actions:      { display:'flex', gap:5, alignItems:'center', flexShrink:0, flexWrap:'wrap' },
  iconBtn:      { width:32, height:32, borderRadius:8, border:'1px solid var(--bdr)', background:'rgba(124,111,247,0.08)', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'inherit' },
  resetInp:     { padding:'5px 9px', borderRadius:8, border:'1px solid var(--bdr)', fontFamily:'inherit', fontSize:11, width:130, outline:'none', background:'var(--input-bg)', color:'var(--txt)' },

  recentRow:    { display:'flex', alignItems:'center', gap:10, padding:'9px 16px', borderBottom:'1px solid var(--bdr)' },
  recentAvatar: { width:32, height:32, borderRadius:'50%', background:'rgba(124,111,247,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13, color:'#7C6FF7', flexShrink:0 },
  recentName:   { fontSize:13, fontWeight:700, color:'var(--txt)' },
  recentMeta:   { fontSize:11, color:'var(--txl)', marginTop:1 },
  recentTime:   { fontSize:11, color:'var(--txl)', flexShrink:0 },

  paginator:     { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 16px', borderTop:'1px solid var(--bdr)', flexWrap:'wrap', gap:8 },
  paginatorInfo: { fontSize:11, color:'var(--txl)', fontWeight:600 },
  paginatorBtns: { display:'flex', gap:3, alignItems:'center' },
  pageBtn:       { minWidth:28, height:28, borderRadius:7, border:'1px solid var(--bdr)', background:'rgba(124,111,247,0.08)', color:'var(--txm)', fontWeight:700, fontSize:12, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', padding:'0 6px' },
  pageBtnActive: { background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', border:'none' },
  pageDots:      { fontSize:12, color:'var(--txl)', padding:'0 3px', userSelect:'none' },
};
